/**
 * Group Chat Tab Component
 * 모임 그룹방 채팅 컴포넌트
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { Send, Paperclip, Mic, Phone, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { apiRequest } from "@/lib/queryClient";

interface ChatMessage {
  id: number;
  group_id: number;
  user_uid: string;
  message: string;
  thread_id: number | null;
  is_notice: number;
  attachments_json: string | null;
  created_at: number;
  email?: string;
  name?: string;
  avatar_url?: string;
}

interface GroupChatTabProps {
  groupId: number | null;
  isMember: boolean;
  isOwner: boolean;
  isAdmin: boolean;
}

export function GroupChatTab({ groupId, isMember, isOwner, isAdmin }: GroupChatTabProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [message, setMessage] = useState("");
  const [isNotice, setIsNotice] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  // 메시지 목록 조회
  const { data: messagesData, isLoading } = useQuery<{ messages: ChatMessage[]; pagination: any }>({
    queryKey: ["/api/community/groups", groupId, "chat"],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}/chat`);
      if (!response.ok) throw new Error("Failed to fetch messages");
      return response.json();
    },
    enabled: !!groupId && isMember,
    refetchInterval: 5000, // 5초마다 폴링
  });

  const messages = messagesData?.messages || [];

  // SSE 스트리밍 (선택사항, 더 나은 실시간 경험)
  useEffect(() => {
    if (!groupId || !isMember || !user) return;

    // SSE는 나중에 구현, 현재는 폴링 사용
    // const token = await user.getIdToken();
    // const es = new EventSource(`/api/community/groups/${groupId}/chat/stream?token=${token}`);
    // setEventSource(es);
    //
    // es.onmessage = (event) => {
    //   const data = JSON.parse(event.data);
    //   if (data.type === "message") {
    //     queryClient.setQueryData(
    //       ["/api/community/groups", groupId, "chat"],
    //       (old: any) => ({
    //         ...old,
    //         messages: [...(old?.messages || []), data.data],
    //       })
    //     );
    //   }
    // };
    //
    // return () => {
    //   es.close();
    // };
  }, [groupId, isMember, user]);

  // 메시지 전송
  const sendMessageMutation = useMutation({
    mutationFn: async (msg: string) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: msg,
          is_notice: isNotice && (isOwner || isAdmin),
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "메시지 전송 실패");
      }
      return response.json();
    },
    onSuccess: () => {
      setMessage("");
      setIsNotice(false);
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "chat"] });
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 통화 링크 생성
  const createCallMutation = useMutation({
    mutationFn: async () => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}/calls/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          duration_minutes: 15,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "통화 링크 생성 실패");
      }
      return response.json();
    },
    onSuccess: (data) => {
      const callUrl = data.call_url || data.jitsi_url;
      if (callUrl) {
        // 통화 링크를 메시지로 전송
        sendMessageMutation.mutate(`📞 통화 링크: ${callUrl}`);
        toast({
          title: "통화 링크가 생성되었습니다",
          description: "그룹방에 링크가 공유되었습니다",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "통화 링크 생성 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 스크롤을 맨 아래로
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    sendMessageMutation.mutate(message);
  };

  if (!isMember) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">그룹방에 참여하려면 모임에 가입해주세요.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col h-[600px]">
      {/* 메시지 목록 */}
      <div className="flex-1 overflow-y-auto space-y-4 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>아직 메시지가 없습니다. 첫 메시지를 작성해보세요!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.user_uid === user?.uid ? "flex-row-reverse" : ""}`}
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={msg.avatar_url || undefined} />
                <AvatarFallback>
                  {(msg.name || msg.email || "U").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className={`flex-1 ${msg.user_uid === user?.uid ? "items-end" : ""}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">
                    {msg.name || msg.email || "익명"}
                  </span>
                  {msg.is_notice === 1 && (
                    <Badge variant="default" className="text-xs">공지</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(msg.created_at * 1000), {
                      addSuffix: true,
                      locale: ko,
                    })}
                  </span>
                </div>
                <div
                  className={`rounded-lg p-3 ${
                    msg.is_notice === 1
                      ? "bg-primary/10 border border-primary"
                      : msg.user_uid === user?.uid
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 입력창 */}
      <div className="border-t p-4">
        {(isOwner || isAdmin) && (
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id="is-notice"
              checked={isNotice}
              onChange={(e) => setIsNotice(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="is-notice" className="text-sm text-muted-foreground">
              공지로 전송
            </label>
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="메시지를 입력하세요..."
            className="flex-1 min-h-[60px] resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              disabled={!message.trim() || sendMessageMutation.isPending}
              size="icon"
            >
              {sendMessageMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => createCallMutation.mutate()}
              disabled={createCallMutation.isPending}
              title="통화 링크 생성"
            >
              {createCallMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Phone className="w-4 h-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}






